import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, ChevronRight, UserPen, ShieldCheck, Wrench, Award, Images, CalendarDays, Banknote, KeyRound, LogOut, BriefcaseBusiness, IdCard, Store, Headset } from "lucide-react";
import { api, errMsg, uploadFile } from "@/lib/api";
import { addressText } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { UAvatar, Verified, Img } from "@/components/common";

function PhotoEdit({ field, testid, className, children }) {
  const { refreshUser } = useAuth();
  const ref = useRef(null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const path = await uploadFile(f, field);
      await api.put("/me", { [field]: path });
      await refreshUser();
      toast.success("Foto diperbarui");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className={`relative ${className}`}>
      {children}
      <button data-testid={testid} onClick={() => ref.current?.click()} className="absolute bottom-1 right-1 w-8 h-8 rounded-full bg-white/95 shadow grid place-items-center hover:scale-110 transition-transform"><Camera className="w-4 h-4" /></button>
      <input ref={ref} type="file" accept="image/*" hidden onChange={pick} />
    </div>
  );
}

const KYC_LABEL = { none: "Belum diajukan", pending: "Menunggu verifikasi", approved: "Terverifikasi", rejected: "Ditolak - ajukan ulang" };

function MenuItem({ to, icon: Icon, label, sub, testid, onClick, danger }) {
  const cls = `flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors ${danger ? "text-red-600" : ""}`;
  const inner = (<><span className={`w-9 h-9 rounded-xl grid place-items-center ${danger ? "bg-red-50" : "bg-slate-100"}`}><Icon className="w-4 h-4" /></span>
    <span className="flex-1 text-left"><span className="block text-sm font-bold">{label}</span>{sub && <span className="block text-xs text-slate-500">{sub}</span>}</span>
    {!danger && <ChevronRight className="w-4 h-4 text-slate-400" />}</>);
  return to ? <Link to={to} data-testid={testid} className={cls}>{inner}</Link> : <button data-testid={testid} onClick={onClick} className={`w-full ${cls}`}>{inner}</button>;
}

export default function Profile() {
  const { user, mode, logout } = useAuth();
  const nav = useNavigate();
  const isW = mode === "worker";
  const doLogout = async () => { await logout(); nav("/login"); };
  return (
    <div data-testid="profile-page" className="-mx-4 -mt-4 fade-up">
      <PhotoEdit field="banner" testid="edit-banner-btn" className="h-36">
        <Img path={user.banner} className="w-full h-36 bg-gradient-to-r from-emerald-500 via-teal-600 to-slate-800" />
      </PhotoEdit>
      <div className="px-4 -mt-10">
        <PhotoEdit field="avatar" testid="edit-avatar-btn" className="w-24 h-24 p-1 bg-white rounded-full">
          <UAvatar path={user.avatar} name={user.name} className="w-[88px] h-[88px]" />
        </PhotoEdit>
        <div className="mt-2 flex items-center gap-1.5">
          <h1 data-testid="profile-name" className="font-display text-2xl font-extrabold">{user.name}</h1>
          <Verified show={user.kyc_status === "approved"} className="w-6 h-6" />
        </div>
        <p className="text-sm text-slate-500">{user.email} · {isW ? "Mode Worker" : "Mode Pengguna"}</p>
        {addressText(user.address) && <p className="text-xs text-slate-500 mt-1">{addressText(user.address)}</p>}
      </div>
      <div className="px-4 mt-5 space-y-4">
        <div className="bg-white rounded-2xl border divide-y overflow-hidden">
          <MenuItem to="/profile/edit" icon={UserPen} label={isW ? "Edit Profil Pekerja" : "Edit Profil"} sub="Nama, alamat, nomor WhatsApp" testid="menu-edit-profile" />
          <MenuItem to="/profile/kyc" icon={ShieldCheck} label="Verifikasi Identitas (KYC)" sub={KYC_LABEL[user.kyc_status]} testid="menu-kyc" />
          {!user.is_worker && user.kyc_status === "approved" && <MenuItem to="/profile/worker-register" icon={BriefcaseBusiness} label="Daftar sebagai Worker" sub="Mulai terima pekerjaan" testid="menu-worker-register" />}
        </div>
        {isW && (
          <div className="bg-white rounded-2xl border divide-y overflow-hidden" data-testid="worker-extra-menu">
            <MenuItem to="/profile/id-card" icon={IdCard} label="ID Card" sub="Kartu identitas worker resmi" testid="menu-id-card" />
            <MenuItem to="/profile/marketplace" icon={Store} label="Marketplace" sub="Belanja kebutuhan kerja" testid="menu-marketplace" />
            <MenuItem to="/profile/support" icon={Headset} label="Layanan Pelanggan" sub="Hubungi tim bantuan" testid="menu-support" />
          </div>
        )}
        {isW && (
          <div className="bg-white rounded-2xl border divide-y overflow-hidden" data-testid="worker-menu">
            <MenuItem to="/profile/skills" icon={Wrench} label="Keahlian" sub={(user.worker?.skills || []).join(", ")} testid="menu-skills" />
            <MenuItem to="/profile/certificates" icon={Award} label="Sertifikat" sub={`${user.worker?.certificates?.length || 0} sertifikat`} testid="menu-certificates" />
            <MenuItem to="/profile/portfolio" icon={Images} label="Portofolio" sub="Pekerjaan yang telah diselesaikan" testid="menu-portfolio" />
            <MenuItem to="/profile/schedule" icon={CalendarDays} label="Jadwal Ketersediaan" sub="Atur jam kerja" testid="menu-schedule" />
            <MenuItem to="/profile/payout" icon={Banknote} label="Penarikan Saldo" sub="Ke rekening bank / e-wallet" testid="menu-payout" />
          </div>
        )}
        <div className="bg-white rounded-2xl border divide-y overflow-hidden">
          <MenuItem to="/profile/password" icon={KeyRound} label="Ubah Password" testid="menu-password" />
          <MenuItem icon={LogOut} label="Keluar" danger onClick={doLogout} testid="logout-btn" />
        </div>
      </div>
    </div>
  );
}
